import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plus, Check, X, Pencil, Bell } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import Modal from './Modal';
import AccountPicker from './AccountPicker';

// The website transfer queue on the Domains page: one row per site moving
// from its current address to a new one. "Mark transferred" updates the
// school's site address, stamps the domain's cutover date and emails the
// notice list (a setting admins edit here).

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-[13px] placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30';
const labelCls = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';
const STATUS = {
  queued: { label: 'Queued', tone: 'bg-white/[0.05] text-gray-300 border-white/[0.1]' },
  in_progress: { label: 'In progress', tone: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  transferred: { label: 'Transferred', tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' },
  cancelled: { label: 'Cancelled', tone: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
};
const short = (url) => String(url || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');

export default function SiteTransfersPanel({ onTransferred }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'nexsav_admin';
  const [transfers, setTransfers] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | transfer row
  const [busyId, setBusyId] = useState(null);
  const [recipients, setRecipients] = useState(null); // { value: [], effective: { emails, source } }
  const [editRecipients, setEditRecipients] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/site-transfers${showDone ? '' : '?status=open'}`);
      setTransfers(res?.transfers || []);
    } catch (err) {
      setNotice({ type: 'err', text: err?.message || 'Could not load the transfer queue' });
    } finally {
      setLoading(false);
    }
  }, [showDone]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/api/admin/settings/site_transfer_notify_emails').then(setRecipients).catch(() => {});
  }, []);

  const complete = async (t) => {
    if (busyId) return;
    if (!window.confirm(`Mark ${t.accountName}’s website as transferred to ${short(t.newUrl)}?\n\nThis updates the school’s site address in the portal and emails the notice list.`)) return;
    setBusyId(t.id);
    setNotice(null);
    try {
      const res = await api.post(`/api/admin/site-transfers/${t.id}/complete`, {});
      const who = res?.notified?.length ? `Told ${res.notified.join(', ')}.` : 'Nobody was emailed (no recipients).';
      setNotice({ type: 'ok', text: `${t.accountName} now points at ${short(t.newUrl)}. ${who}` });
      await load();
      onTransferred?.(res);
    } catch (err) {
      setNotice({ type: 'err', text: err instanceof ApiError ? err.message : 'Could not mark it transferred' });
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (t, status) => {
    if (busyId) return;
    setBusyId(t.id);
    try {
      await api.patch(`/api/admin/site-transfers/${t.id}`, { status });
      await load();
    } catch (err) {
      setNotice({ type: 'err', text: err instanceof ApiError ? err.message : 'Could not update the transfer' });
    } finally {
      setBusyId(null);
    }
  };

  const effective = recipients?.effective;
  return (
    <section className="mb-6 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid="site-transfers" aria-labelledby="site-transfers-title">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 id="site-transfers-title" className="text-[14px] font-semibold text-white">Website transfers</h2>
          <p className="text-[12px] text-gray-500 mt-0.5">Sites moving to a new address. Marking one transferred updates the school’s site address and stamps the domain’s cutover date.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-[12px] text-gray-400 cursor-pointer select-none">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} className="accent-[#B0FC38]" /> Show finished
          </label>
          <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] bg-[#B0FC38] text-gray-900 font-medium hover:bg-[#9BE22E]" data-testid="transfer-new">
            <Plus className="w-3.5 h-3.5" /> New transfer
          </button>
        </div>
      </div>

      {notice && (
        <div role="status" className={`mb-3 px-3 py-2 rounded-lg text-[12px] border ${notice.type === 'ok' ? 'bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-200' : 'bg-red-500/10 border-red-500/20 text-red-300'}`}>{notice.text}</div>
      )}

      {loading && transfers.length === 0 ? (
        <div className="h-10 rounded-lg bg-white/[0.03] animate-pulse" />
      ) : transfers.length === 0 ? (
        <p className="text-[12px] text-gray-500 py-2">Nothing in the queue.</p>
      ) : (
        <ul className="divide-y divide-white/[0.05] list-none m-0 p-0">
          {transfers.map((t) => {
            const st = STATUS[t.status] || STATUS.queued;
            const open = t.status === 'queued' || t.status === 'in_progress';
            const overdue = open && t.targetDate && t.targetDate < new Date().toISOString().slice(0, 10);
            return (
              <li key={t.id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`transfer-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/admin/accounts/${t.accountId}`} className="text-[13px] text-white font-medium hover:text-[#B0FC38] truncate">{t.accountName}</Link>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border ${st.tone}`}>{st.label}</span>
                    {t.targetDate && <span className={`text-[11px] ${overdue ? 'text-amber-300' : 'text-gray-500'}`}>{overdue ? 'was due' : 'due'} {t.targetDate}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 text-[12px] font-mono text-gray-400 flex-wrap">
                    <span className="truncate">{t.currentUrl ? short(t.currentUrl) : '—'}</span>
                    <ArrowRight className="w-3 h-3 shrink-0 text-gray-600" aria-hidden="true" />
                    <a href={t.newUrl} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#B0FC38] truncate">{short(t.newUrl)}</a>
                  </div>
                  {t.notes && <div className="text-[12px] text-gray-500 mt-1">{t.notes}</div>}
                  {t.status === 'transferred' && <div className="text-[11px] text-gray-600 mt-1">Transferred {String(t.transferredAt || '').slice(0, 10)}{t.transferredByName ? ` by ${t.transferredByName}` : ''}</div>}
                </div>
                {open && (
                  <div className="flex items-center gap-1 shrink-0">
                    {t.status === 'queued' && <button type="button" onClick={() => setStatus(t, 'in_progress')} disabled={busyId === t.id} className="px-2.5 py-1.5 rounded-lg text-[12px] text-gray-300 border border-white/[0.1] hover:text-white disabled:opacity-50">Start</button>}
                    <button type="button" onClick={() => complete(t)} disabled={busyId === t.id} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] bg-[#B0FC38]/90 text-gray-900 font-medium hover:bg-[#B0FC38] disabled:opacity-50" data-testid={`transfer-complete-${t.id}`}>
                      <Check className="w-3.5 h-3.5" aria-hidden="true" /> {busyId === t.id ? 'Working…' : 'Mark transferred'}
                    </button>
                    <button type="button" onClick={() => setEditing(t)} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06]" aria-label={`Edit transfer for ${t.accountName}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => setStatus(t, 'cancelled')} disabled={busyId === t.id} className="p-1.5 rounded-lg text-gray-500 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50" aria-label={`Cancel transfer for ${t.accountName}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 pt-3 border-t border-white/[0.05] flex items-center gap-2 text-[12px] text-gray-500 flex-wrap" data-testid="transfer-recipients">
        <Bell className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        {!effective ? <span>Notice list…</span> : (
          <span>
            On completion, email {effective.emails.length ? effective.emails.join(', ') : 'nobody'}
            {effective.source === 'staff' ? ' (every active admin and ops user, until a list is set)' : ''}.
          </span>
        )}
        {isAdmin && recipients && <button type="button" onClick={() => setEditRecipients(true)} className="text-[#B0FC38] hover:underline" data-testid="transfer-recipients-edit">Change</button>}
      </div>

      {editing && (
        <TransferModal
          transfer={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(t, created) => { setEditing(null); setNotice({ type: 'ok', text: created ? `${t.accountName} added to the transfer queue.` : 'Transfer updated.' }); load(); }}
        />
      )}
      {editRecipients && (
        <RecipientsModal
          value={recipients?.value || []}
          onClose={() => setEditRecipients(false)}
          onSaved={(res) => { setRecipients(res); setEditRecipients(false); }}
        />
      )}
    </section>
  );
}

function TransferModal({ transfer, onClose, onSaved }) {
  const editing = Boolean(transfer?.id);
  const [form, setForm] = useState({
    accountId: transfer?.accountId || '',
    currentUrl: transfer?.currentUrl || '',
    newUrl: transfer?.newUrl || '',
    targetDate: transfer?.targetDate || '',
    notes: transfer?.notes || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const body = { newUrl: form.newUrl.trim(), targetDate: form.targetDate || null, notes: form.notes.trim() || null };
      if (form.currentUrl.trim() || editing) body.currentUrl = form.currentUrl.trim() || null;
      const res = editing
        ? await api.patch(`/api/admin/site-transfers/${transfer.id}`, body)
        : await api.post('/api/admin/site-transfers', { accountId: form.accountId, ...body });
      onSaved?.(res.transfer, !editing);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Network error, please try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={editing ? `Edit transfer · ${transfer.accountName}` : 'New website transfer'}>
      <form onSubmit={submit} className="space-y-4" data-testid="transfer-form">
        {!editing && (
          <div>
            <label className={labelCls}>School</label>
            <AccountPicker value={form.accountId} onChange={(id) => setForm((f) => ({ ...f, accountId: id || '' }))} placeholder="Search schools…" />
          </div>
        )}
        <div>
          <label htmlFor="transfer-new-url" className={labelCls}>New address</label>
          <input id="transfer-new-url" type="text" value={form.newUrl} onChange={set('newUrl')} placeholder="https://ps11x.org" required className={inputCls} />
        </div>
        <div>
          <label htmlFor="transfer-current-url" className={labelCls}>Current address <span className="text-gray-600">(optional; the school’s site address is used)</span></label>
          <input id="transfer-current-url" type="text" value={form.currentUrl} onChange={set('currentUrl')} placeholder="https://ps11x.nexsav.net" className={inputCls} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="transfer-target" className={labelCls}>Target date</label>
            <input id="transfer-target" type="date" value={form.targetDate} onChange={set('targetDate')} className={inputCls} />
          </div>
        </div>
        <div>
          <label htmlFor="transfer-notes" className={labelCls}>Notes</label>
          <textarea id="transfer-notes" rows={2} value={form.notes} onChange={set('notes')} className={`${inputCls} resize-y`} placeholder="DNS at GoDaddy, principal wants Friday…" />
        </div>
        {error && <div role="alert" className="text-[12px] text-red-300">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-[12px] text-gray-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={saving || !form.newUrl.trim() || (!editing && !form.accountId)} className="px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50" data-testid="transfer-save">{saving ? 'Saving…' : editing ? 'Save changes' : 'Add to queue'}</button>
        </div>
      </form>
    </Modal>
  );
}

function RecipientsModal({ value, onClose, onSaved }) {
  const [text, setText] = useState(value.join(', '));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await api.put('/api/admin/settings/site_transfer_notify_emails', { value: text });
      onSaved?.(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Network error, please try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open onClose={onClose} title="Who is told when a site is transferred" maxWidth="max-w-md">
      <form onSubmit={submit} className="space-y-4" data-testid="recipients-form">
        <div>
          <label htmlFor="transfer-recipients-input" className={labelCls}>Email addresses, comma-separated</label>
          <input id="transfer-recipients-input" type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder="husain@nexsav.net, jerrell@nexsav.net" className={inputCls} />
          <p className="text-[11.5px] text-gray-500 mt-1.5">Leave empty to email every active admin and ops user.</p>
        </div>
        {error && <div role="alert" className="text-[12px] text-red-300">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-[12px] text-gray-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50" data-testid="recipients-save">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}
